import axios, { AxiosResponse } from "axios";
import { environment } from "../../environment";
import { useLogin } from "../login/login";
import { ICrmError, ISalesRepCandidate, ISalesRepListItem, SalesLevel } from "./types";

const adminUrl = `${environment.v2Url}/crm-admin`;

export type CrmAdminKeyFunctions = {
    listReps: () => Promise<ISalesRepListItem[] | ICrmError>;
    grantRep: (email: string, level: SalesLevel) => Promise<ISalesRepListItem | ICrmError>;
    updateRep: (userId: number, patch: { level?: SalesLevel; active?: boolean }) => Promise<ISalesRepListItem | ICrmError>;
    revokeRep: (userId: number) => Promise<true | ICrmError>;
    searchUsers: (q: string) => Promise<ISalesRepCandidate[] | ICrmError>;
};

/**
 * Manager-only sales team administration. Every route is mounted behind
 * RequireSalesAccess('manager') server-side; this only avoids showing a screen
 * that would 403 on every call.
 */
export const useCrmAdmin = (): [CrmAdminKeyFunctions] => {
    const [, authToken, loginFunctions] = useLogin();
    const headers = { 'X-Wallmates-Auth': authToken };

    const handle = (err: any): ICrmError => {
        const status = err?.response?.status ?? 0;
        if (status === 401) loginFunctions.logout();
        return {
            ok: false,
            status,
            message: err?.response?.data?.message
                || (status === 0 ? 'Could not reach Wallmates. Check your connection.' : 'Something went wrong.')
        };
    };

    const listReps = async (): Promise<ISalesRepListItem[] | ICrmError> => {
        try {
            const result: AxiosResponse<ISalesRepListItem[]> = await axios.get(`${adminUrl}/reps`, { headers });
            return result.data;
        } catch (err: any) { return handle(err); }
    };

    const grantRep = async (email: string, level: SalesLevel): Promise<ISalesRepListItem | ICrmError> => {
        try {
            const result: AxiosResponse<ISalesRepListItem> =
                await axios.post(`${adminUrl}/reps`, { email, level }, { headers });
            return result.data;
        } catch (err: any) { return handle(err); }
    };

    const updateRep = async (
        userId: number,
        patch: { level?: SalesLevel; active?: boolean }
    ): Promise<ISalesRepListItem | ICrmError> => {
        try {
            const result: AxiosResponse<ISalesRepListItem> =
                await axios.put(`${adminUrl}/reps/${userId}`, patch, { headers });
            return result.data;
        } catch (err: any) { return handle(err); }
    };

    const revokeRep = async (userId: number): Promise<true | ICrmError> => {
        try {
            await axios.delete(`${adminUrl}/reps/${userId}`, { headers });
            return true;
        } catch (err: any) { return handle(err); }
    };

    const searchUsers = async (q: string): Promise<ISalesRepCandidate[] | ICrmError> => {
        try {
            const result: AxiosResponse<ISalesRepCandidate[]> =
                await axios.get(`${adminUrl}/users`, { headers, params: { q } });
            return result.data;
        } catch (err: any) { return handle(err); }
    };

    return [{ listReps, grantRep, updateRep, revokeRep, searchUsers }];
};
